/* eslint-disable no-useless-computed-key */
import { h } from "preact";
import { useEffect, useContext, useState, useRef } from "preact/hooks";

import { useUser, useFirestore } from "../lib/preactfire";
import {
  Context,
  setLoginModalVisible,
  setUserInfoAction,
} from "../lib/globalState";
import { init } from "../lib/pell";
import {
  clearInput,
  disablePellEditor,
  enablePellEditor,
  removeOverlay,
  stripTags,
} from "../lib/helper";

export default function ReplyBox({ id, posterName }) {
  const user = useUser();
  const db = useFirestore();

  const { state, dispatch } = useContext(Context);

  const [reply, setReply] = useState();
  const [enable, setEnable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [isHover, setHover] = useState(false);

  const editor = useRef();

  useEffect(() => {
    init({
      element: document.getElementById(`reply-editor-${id}`),
      defaultParagraphSeparator: "p",
      actions: [
        "bold",
        "italic",
        "underline",
        "strikethrough",
        "link",
        "quote",
        "ulist",
      ],
      placeholder: `Reply to ${posterName}`,
      onChange: setReply,
    });
  }, [id, posterName]);

  const onReply = async () => {
    if (reply && stripTags(reply).length > 0) {
      if (user) {
        const { creationTime, lastSignInTime } = user.metadata;

        disablePellEditor(editor.current);
        setEnable(false);

        let response = await fetch(
          "https://api.ipinfodb.com/v3/ip-city/?key=25864308b6a77fd90f8bf04b3021a48c1f2fb302a676dd3809054bc1b07f5b42&format=json"
        );
        const {
          regionName: geo_region,
          countryName: geo_country,
          cityName: geo_city,
          ipAddress: ip_address,
        } = await response.json();

        const location = {
          geo_region,
          geo_country,
          geo_city,
          ip_address,
          geo_language: "",
        };

        // Find device type
        let device;
        if (window.innerWidth > 768) {
          device = "Desktop";
        } else if (window.innerWidth < 767 && window.innerWidth > 481) {
          device = "iPad/Tablet";
        } else if (window.innerWidth < 480) {
          device = "Mobile";
        }

        // User Agent, Device, OS and Screen Resolution
        const browser = window.navigator.appCodeName;
        const operating_system = window.navigator.platform;
        const device_type = device;
        const device_name = window.navigator.platform;
        const screen_res = `${screen.width}x${screen.height}`;
        const technology = {
          ["tech_browser"]: browser,
          ["tech_os"]: operating_system,
          ["tech_device_type"]: device_type,
          ["tech_device_name"]: device_name,
          ["tech_screen_res"]: screen_res,
        };
        const user_id = user.uid;
        const display_name = user.displayName;
        const profile_img = user.photoURL;
        const timestamp = Date.now();

        const type = "reply";
        const checkData = {
          comment: {
            text: reply,
          },
          requestedAttributes: {
            TOXICITY: {},
          },
        };

        const docRef = db.collection("comment-user").doc(user.uid);
        const doc = await docRef.get();

        if (doc.exists || user) {
          const total_comments = doc.data()?.total_comments ?? 0;
          const hidden_comments = doc.data()?.hidden_comments ?? 0;

          response = await fetch(
            "https://commentanalyzer.googleapis.com/v1alpha1/comments:analyze?key=AIzaSyBrQhnYKisBxAYAhFc3DiygTWhoSYd2PiM",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(checkData),
            }
          );
          const result = await response.json();
          const toxicity =
            Math.round(
              result.attributeScores.TOXICITY.summaryScore.value * 100
            ) / 100;
          const language = result.languages["0"];

          let status = "public";
          if (toxicity <= 0.6) {
            const docRef = db.collection("comment").doc(state.apiKey);
            const doc = await docRef.get();

            if (doc.exists) {
              if (doc.data().restricted_word) {
                const reply_value_array = reply.split(" ");

                let isReview = false;
                for (let i = 0; i < doc.data().restricted_word.length; i++) {
                  for (let j = 0; j < reply_value_array.length; j++) {
                    if (
                      doc.data().restricted_word[i] ==
                      reply_value_array[j].toLowerCase()
                    ) {
                      isReview = true;
                    }
                  }
                }
                if (isReview) {
                  status = "review";
                }
              }
            }
          } else if (toxicity > 0.6 && toxicity <= 0.85) {
            status = "review";
          } else {
            status = "hidden";
            setLoading(true);
            setLoadingMessage("Your reply has objectionable words");
            setTimeout(() => {
              clearInput(editor.current);
              enablePellEditor(editor.current);
              setEnable(true);
              setLoading(false);
            }, 3000);
            return;
          }

          setLoading(true);
          setLoadingMessage("Submitting your reply...");

          try {
            const docRef = db.collection("comment").doc();

            await docRef.set({
              api_key: state.apiKey,
              page: "http://demo.goemotify.com/post1.html",
              content: reply,
              title:
                "Man must explore, and this is exploration at its greatest",
              image:
                "https://s3.amazonaws.com/media-p.slid.es/uploads/429997/images/2111824/negativespace-161.jpg",
              ip_address,
              story_id: "90801",
              timestamp,
              toxicity,
              language,
              status,
              type,
              user_id,
              user_name: display_name,
              profile_img,
              ...location,
              ...technology,
            });

            const replies = {
              comment_id: docRef.id,
              url_path: `comment/${docRef.id}`,
              timestamp,
            };

            await db
              .collection("comment-user")
              .doc(user.uid)
              .set({
                user_id: user.uid,
                first_name: user.displayName.substr(
                  0,
                  user.displayName.lastIndexOf(" ")
                ),
                last_name:
                  user.displayName.substr(
                    user.displayName.lastIndexOf(" ") + 1
                  ) ?? "",
                email: user.email,
                phone_no: user.phoneNumber,
                profile_img: user.photoURL,
                registered_at: creationTime,
                last_seen: lastSignInTime,
                login_method: user.providerData[0].providerId,
                registered_ip: ip_address,
                ...location,
                total_comments: total_comments + 1,
                hidden_comments,
                total_flags: 0,
                risk_profile: "",
                blocked_by: "",
              });

            const updateDocRef = db.collection("comment").doc(`${id}`);
            const doc = await updateDocRef.get();

            if (doc.data().replies) {
              const newData = doc.data().replies;
              newData["comment_id"] = docRef.id;
              newData["url_path"] = `comment/${docRef.id}`;
              newData["timestamp"] = timestamp;
              updateDocRef.update({
                replies: [...doc.data().replies, replies],
              });
            } else {
              updateDocRef.update({ replies: [replies] });
            }

            setLoadingMessage("You reply posted successfully!");
            setReply("");
            clearInput(editor.current);

            setTimeout(() => {
              enablePellEditor(editor.current);
              setEnable(true);
              setLoading(false);
            }, 3000);
          } catch (e) {
            console.log(e);
          }
        }
      } else {
        dispatch(setLoginModalVisible(true));
      }
    }
  };

  return (
    <div>
      <div className="reply-box" id={`reply-box-${id}`}>
        <div ref={editor} id={`reply-editor-${id}`} className="reply-box-pell">
          <div className="reply-action-container">
            <div
              className="reply-login-action"
              id={`comment-login-prompt_${id}`}
            >
              {user ? (
                <div
                  className="comment-box-user"
                  onClick={() => {
                    removeOverlay();
                    dispatch(
                      setUserInfoAction(
                        {
                          name: user.displayName,
                          profile_img: user.photoURL,
                          user_id: user.uid,
                        },
                        true
                      )
                    );
                  }}
                >
                  <img src={user?.photoURL} className="comment-box-user-img" />
                  <span
                    className="comment-box-user-name"
                    style={`color: ${state.color}`}
                  >
                    {user?.displayName}
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  className="login-modal comment-login"
                  onClick={() => {
                    removeOverlay();
                    dispatch(setLoginModalVisible(true));
                  }}
                  onMouseOver={() => setHover(true)}
                  onMouseOut={() => setHover(false)}
                >
                  <span style={isHover && `color: ${state.color}`}>
                    Log in to reply
                  </span>
                </button>
              )}
              <div>
                <span class="reply-length-count">
                  {stripTags(reply)?.length > 0 && stripTags(reply)?.length}
                </span>
              </div>
            </div>

            <div className="reply-submit-section">
              <input
                disabled={!enable}
                type="submit"
                name="commit"
                value="Submit"
                class="reply-submission"
                style={`background-color: ${state.color}; border-color: ${state.color}`}
                onClick={onReply}
              />
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="alert success" style="margin-top: 1rem">
          <div className="message">{loadingMessage}</div>
        </div>
      )}
    </div>
  );
}
